import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DoorOpen, Eye, FileAudio, Flame, History, Info, Megaphone, Pencil, Radio, ShieldCheck, Siren, Trash2, UploadCloud, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ask } from '../components/Confirm';
import { useAssetUrls } from '../components/EntityForm';
import { Select } from '../components/Select';
import { ErrorBox, PageHeader, timeAgo } from '../components/ui';
import type { Message } from './MarketingPage';

type Kind = 'FIRE' | 'EVACUATION' | 'SHELTER' | 'CUSTOM';
const PRESETS: Record<Kind, { label: string; icon: ReactNode; title: string; body: string }> = {
  FIRE: { label: 'Fire alarm', icon: <Flame size={17} />, title: 'Fire alarm', body: 'A fire alarm has been activated. Please leave your room calmly and use the nearest emergency exit. Do not use the lifts.' },
  EVACUATION: { label: 'Evacuation', icon: <DoorOpen size={17} />, title: 'Evacuation', body: 'Please evacuate the building now and proceed to the assembly point. Follow the instructions of hotel staff.' },
  SHELTER: { label: 'Shelter in place', icon: <ShieldCheck size={17} />, title: 'Shelter in place', body: 'For your safety, please stay in your room, keep the door closed and wait for further instructions.' },
  CUSTOM: { label: 'Custom message', icon: <Pencil size={17} />, title: '', body: '' },
};

interface Media {
  id: string;
  url: string;
  kind: 'IMAGE' | 'VIDEO' | 'AUDIO';
  name: string;
  bytes: number;
}
const MAX = 100 * 1024 * 1024;
const size = (b: number) => (b >= 1024 ** 2 ? `${(b / 1024 ** 2).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function EmergencyPage() {
  const site = useSite();
  return (
    <>
      <div className="emg-head">
        <span className="emg-head-icon">
          <Siren size={26} />
        </span>
        <PageHeader title="Emergency" sub="Send a full-screen alert to every TV in the hotel. It overrides all content and stays until you end it." />
      </div>
      <NeedsSite>{site && <Emergency key={site.id} siteId={site.id} />}</NeedsSite>
    </>
  );
}

function Emergency({ siteId }: { siteId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const url = useAssetUrls();
  const [kind, setKind] = useState<Kind>('FIRE');
  const [title, setTitle] = useState(PRESETS.FIRE.title);
  const [body, setBody] = useState(PRESETS.FIRE.body);
  const [media, setMedia] = useState<Media | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [upErr, setUpErr] = useState<unknown>(null);
  const input = useRef<HTMLInputElement>(null);
  const msgs = useQuery({
    queryKey: ['messages', siteId, 'emergency'],
    queryFn: () => api.get<{ items: (Message & { mediaAssetId?: string | null })[] }>(`/sites/${siteId}/messages?view=emergency`),
    refetchInterval: 15_000,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['messages', siteId] });
  const start = useMutation({ mutationFn: () => api.post(`/sites/${siteId}/emergency`, { title: title.trim(), body: body.trim(), mediaAssetId: media?.id ?? null }), onSuccess: invalidate });
  const end = useMutation({ mutationFn: () => api.delete(`/sites/${siteId}/emergency`), onSuccess: invalidate });
  const active = (msgs.data?.items ?? []).find((m) => m.status === 'ACTIVE');
  const history = (msgs.data?.items ?? []).filter((m) => m.status !== 'ACTIVE').slice(0, 10);
  const operator = can('OPERATOR');

  const pick = (k: Kind) => {
    setKind(k);
    if (k !== 'CUSTOM') {
      setTitle(PRESETS[k].title);
      setBody(PRESETS[k].body);
    } else {
      setTitle('');
      setBody('');
    }
  };
  const upload = async (file: File) => {
    setUpErr(null);
    const type = file.type.startsWith('image/') ? 'IMAGE' : file.type.startsWith('video/') || /\.(mp4|m4v)$/i.test(file.name) ? 'VIDEO' : file.type.startsWith('audio/') || /\.(mp3|aac|m4a)$/i.test(file.name) ? 'AUDIO' : null;
    if (!type) return setUpErr(new Error('Choose a picture (PNG, JPG), a video (MP4) or an audio file (MP3, AAC).'));
    if (file.size > MAX) return setUpErr(new Error(`The file is ${size(file.size)}; the limit is 100 MB.`));
    const path = type === 'IMAGE' ? '/assets' : type === 'VIDEO' ? '/assets/video' : '/assets/audio';
    setProgress(0);
    try {
      const a = await api.uploadWithProgress<{ id: string; url: string }>(path, file, setProgress).done;
      setMedia({ id: a.id, url: a.url, kind: type, name: file.name, bytes: file.size });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    } catch (e) {
      setUpErr(e);
    } finally {
      setProgress(null);
    }
  };

  const shown = active
    ? { title: active.title ?? '', body: active.body ?? '', image: url(active.imageAssetId), media: null as Media | null }
    : { title, body, image: media?.kind === 'IMAGE' ? media.url : null, media };

  return (
    <>
      {active && (
        <div className="emg-active">
          <Siren size={28} />
          <span style={{ flex: 1 }}>
            <strong>
              Emergency active since {timeAgo(active.createdAt)}: {active.title}
            </strong>
            <span>Every TV shows this alert full screen until you end it.</span>
          </span>
          {operator && (
            <button className="btn" disabled={end.isPending} onClick={() => ask('End the emergency on all TVs? The TVs go back to what they were showing.', () => end.mutate(), { action: 'End emergency' })}>
              <X size={16} /> End emergency
            </button>
          )}
        </div>
      )}

      {!active && (
        <div className="row" style={{ gap: 10, margin: '6px 0 16px' }}>
          {(Object.keys(PRESETS) as Kind[]).map((k) => (
            <button key={k} type="button" className={`emg-tab${kind === k ? ' on' : ''}`} onClick={() => pick(k)}>
              {PRESETS[k].icon} {PRESETS[k].label}
            </button>
          ))}
        </div>
      )}

      <div className="emg-layout">
        {!active ? (
          <div className="card">
            <div className="row" style={{ gap: 10 }}>
              <Megaphone size={20} />
              <strong style={{ fontSize: 17 }}>Broadcast emergency alert</strong>
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              Fill in the details and send the alert to all TVs at once.
            </div>
            <div className="fe-body">
              <label className="camp-field">
                <span className="camp-label">Alert type</span>
                <Select value={kind} onChange={(e) => pick(e.target.value as Kind)} aria-label="Alert type">
                  {(Object.keys(PRESETS) as Kind[]).map((k) => (
                    <option key={k} value={k}>
                      {PRESETS[k].label}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="camp-field">
                <span className="camp-label">Title</span>
                <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Water leak on floor 3" />
              </label>
              <label className="camp-field">
                <span className="camp-label">Message</span>
                <textarea value={body} maxLength={500} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 96 }} />
                <span className="fe-count">{body.length}/500</span>
              </label>
              <div className="camp-field">
                <span className="camp-label">
                  Media <span className="muted">(optional: picture, video or audio)</span>
                </span>
                {media ? (
                  <div className="emg-media">
                    <span className="emg-media-thumb">
                      {media.kind === 'IMAGE' ? <img src={media.url} alt="" /> : media.kind === 'VIDEO' ? <video src={media.url} muted /> : <FileAudio size={26} />}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <strong className="mk-name">{media.name}</strong>
                      <span className="muted" style={{ fontSize: 12.5 }}>
                        {size(media.bytes)} · {media.kind === 'IMAGE' ? 'Picture, behind the text' : media.kind === 'VIDEO' ? 'Video, full screen in a loop' : 'Audio, plays in a loop'}
                      </span>
                      {media.kind !== 'IMAGE' && (media.kind === 'VIDEO' ? <video className="emg-media-play" src={media.url} controls /> : <audio className="emg-media-play" src={media.url} controls />)}
                    </span>
                    <button className="icon-btn" title="Remove" onClick={() => setMedia(null)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="emg-drop"
                    disabled={progress !== null}
                    onClick={() => input.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const f = e.dataTransfer.files[0];
                      if (f) void upload(f);
                    }}
                  >
                    <UploadCloud size={28} />
                    {progress !== null ? (
                      <>
                        <strong>Uploading… {Math.floor(progress * 100)}%</strong>
                        <span className="upload-bar" style={{ width: '60%' }}>
                          <span style={{ width: `${progress * 100}%` }} />
                        </span>
                      </>
                    ) : (
                      <>
                        <strong>Upload a media file</strong>
                        <span>PNG, JPG, MP4 or MP3 (max 100 MB). A video or audio plays in a loop with sound.</span>
                        <span className="btn primary small" style={{ marginTop: 6 }}>
                          Choose file
                        </span>
                      </>
                    )}
                  </button>
                )}
                <ErrorBox error={upErr} />
                <input
                  ref={input}
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/webp,video/mp4,.mp4,audio/mpeg,audio/aac,audio/mp4,.mp3,.aac,.m4a"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void upload(f);
                  }}
                />
              </div>
              <button
                className="btn emg-send"
                disabled={!title.trim() || !body.trim() || !operator || start.isPending || progress !== null}
                onClick={() => ask(`Broadcast “${title.trim()}” now? It shows full screen on every TV of the hotel until you end it.`, () => start.mutate(), { action: 'Broadcast', danger: true })}
              >
                <Radio size={17} /> Broadcast to all TVs
              </button>
              <ErrorBox error={start.error ?? end.error ?? msgs.error} />
            </div>
          </div>
        ) : (
          <div className="card">
            <strong style={{ fontSize: 17 }}>{active.title}</strong>
            <p className="muted" style={{ whiteSpace: 'pre-line' }}>
              {active.body}
            </p>
            <div className="muted" style={{ fontSize: 13 }}>
              Started {new Date(active.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
              {active.mediaAssetId ? ' · with a video or audio file' : active.imageAssetId ? ' · with a picture' : ''}
            </div>
            <ErrorBox error={end.error} />
          </div>
        )}

        <div className="stack">
          <div className="card">
            <div className="row" style={{ gap: 10 }}>
              <Eye size={20} />
              <strong style={{ fontSize: 17 }}>Live preview</strong>
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              How the alert appears on guest TVs.
            </div>
            <div className="emg-tv">
              <div className={`emg-screen${shown.image || shown.media?.kind === 'VIDEO' ? ' with-media' : ''}`}>
                {shown.media?.kind === 'VIDEO' ? <video src={shown.media.url} muted autoPlay loop playsInline /> : shown.image ? <img src={shown.image} alt="" /> : null}
                <div className="emg-text">
                  {!shown.image && shown.media?.kind !== 'VIDEO' && <Siren size={46} />}
                  <strong>{(shown.title || 'Title').toUpperCase()}</strong>
                  <span>{shown.body || 'Message'}</span>
                </div>
              </div>
              <div className="emg-stand" />
            </div>
          </div>
          <div className="card emg-how">
            <div className="row" style={{ gap: 10 }}>
              <Info size={20} color="var(--brand)" />
              <strong style={{ fontSize: 17 }}>How it works</strong>
            </div>
            {[
              'Pick an alert type, or write a custom message.',
              'Optionally add a picture, a video or an audio announcement.',
              'Press “Broadcast to all TVs”: every TV of the hotel shows it at once, over any channel or app.',
              'It stays on screen until you press “End emergency”.',
            ].map((t, i) => (
              <div key={i} className="emg-step">
                <span>{i + 1}</span>
                {t}
              </div>
            ))}
          </div>
          {history.length > 0 && (
            <div className="card">
              <div className="row" style={{ gap: 10 }}>
                <History size={20} />
                <strong style={{ fontSize: 17 }}>Past alerts</strong>
              </div>
              <div className="fac-detail-list" style={{ borderTop: 0 }}>
                {history.map((m) => (
                  <div key={m.id} className="fac-detail-row">
                    <Siren size={15} />
                    <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{m.title}</span>
                    <span className="fac-detail-val" style={{ fontWeight: 500, color: 'var(--muted)' }}>
                      {new Date(m.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                      {m.endAt ? ` · ended ${timeAgo(m.endAt)}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
